import React, { useState, useEffect, useMemo } from 'react';
import { Package, CheckCircle2, Building2 } from 'lucide-react';
import { useProducts } from '../../hooks/useOfflineData.js';

interface Product {
  id: string;
  name: string;
  manufacturer_id: string;
  manufacturer_name: string;
  campaign_name?: string;
}

interface ProductSelectorMultiProps {
  value?: string[];
  onChange: (productIds: string[]) => void;
  allowMultiple?: boolean;
}

export const ProductSelectorMulti: React.FC<ProductSelectorMultiProps> = ({
  value = [],
  onChange,
  allowMultiple = true,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(value);
  const { products, loading } = useProducts();

  useEffect(() => {
    setSelectedIds(value);
  }, [value]);

  const grouped = useMemo(() => {
    const map = new Map<string, { manufacturerName: string; products: Product[] }>();
    for (const p of products as Product[]) {
      if (!map.has(p.manufacturer_id)) {
        map.set(p.manufacturer_id, { manufacturerName: p.manufacturer_name, products: [] });
      }
      map.get(p.manufacturer_id)!.products.push(p);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.manufacturerName.localeCompare(b.manufacturerName),
    );
  }, [products]);

  const handleToggle = (productId: string) => {
    let updated: string[];
    if (allowMultiple) {
      updated = selectedIds.includes(productId)
        ? selectedIds.filter((id) => id !== productId)
        : [...selectedIds, productId];
    } else {
      updated = [productId];
    }
    setSelectedIds(updated);
    onChange(updated);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
        <p className="mt-2 text-sm text-gray-500">Loading products…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Package size={18} className="text-blue-600" />
        <h4 className="font-semibold text-gray-900">
          {allowMultiple ? 'Select Products (multiple allowed)' : 'Select Product'}
        </h4>
      </div>

      {selectedIds.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>{selectedIds.length}</strong> product{selectedIds.length !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}

      {grouped.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Package size={40} className="mx-auto mb-2 opacity-50" />
          <p>No active products available.</p>
        </div>
      ) : (
        grouped.map(({ manufacturerName, products: group }) => (
          <div key={manufacturerName}>
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={13} className="text-brand-500" />
              <span className="text-xs font-black uppercase tracking-widest text-brand-700">
                {manufacturerName}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {group.map((product) => {
                const isSelected = selectedIds.includes(product.id);
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleToggle(product.id)}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && <CheckCircle2 size={14} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">{product.name}</p>
                        {product.campaign_name && (
                          <p className="text-xs text-gray-500 mt-0.5 italic">{product.campaign_name}</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
